import { Trans, TFunction, useTranslation } from 'react-i18next';
import { Button, Table, Spin, Dropdown, Menu, Modal } from 'antd';
import { getCellValue } from 'utils/tableUtils';
import styles from './AchievementsPage.module.less';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { projectSelector } from 'redux/selectors/projectSelector';
import { EllipsisOutlined, InfoCircleOutlined } from '@ant-design/icons';
import api, { AchievementDefinition } from 'api';
import Breadcrumb from 'components/Breadcrumb';
import { IRoutes } from 'components/Breadcrumb/Breadcrumb';
import OmniNotification from 'components/OmniNotification';

type ParamsProps = {
	projectId: string;
};

type HandleDeleteParams = {
	productId: string;
	apiName: string;
};

export default function AchievementsPage() {
	const { t } = useTranslation();
	const route = useRouteMatch();
	const history = useHistory();
	const [achievements, setAchievements] = useState<any>();
	const projectInfo = useSelector(projectSelector);
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const { projectId } = useParams<ParamsProps>();
	function navigateToCreateAchievement() {
		history.push(`achievements/create`);
	}
	const columns = useMemo(() => {
		return [
			{
				title: 'Icon',
				width: 150,
				render: (record: AchievementDefinition) => {
					if (
						record.lockedImageURL != null &&
						record.unlockedImageURL != null
					) {
						return (
							<div>
								<img
									className={styles.icons}
									data-testid="lockedIcon"
									src={record.lockedImageURL}
								/>
								<img
									className={styles.icons}
									data-testid="unlockedIcon"
									src={record.unlockedImageURL}
								/>
							</div>
						);
					}
					if (record.lockedImageURL != null) {
						return (
							<img
								className={styles.icons}
								data-testid="lockedIcon"
								src={record.lockedImageURL}
							/>
						);
					}
					if (record.unlockedImageURL != null) {
						return (
							<img
								className={styles.icons}
								data-testid="unlockedIcon"
								src={record.unlockedImageURL}
							/>
						);
					}
					return '';
				},
			},
			{
				title: 'Title',
				render: (record: AchievementDefinition) => getCellValue(record.title),
				ellipsis: true,
			},
			{
				title: 'Description',
				render: (record: AchievementDefinition) =>
					getCellValue(record.description),
				ellipsis: true,
			},
			{
				title: 'API Name',
				render: (record: AchievementDefinition) => getCellValue(record.name),
				ellipsis: true,
			},
			{
				title: 'Secret?',
				width: 100,
				render: (record: AchievementDefinition) => {
					if (record.isSecret) {
						return t('achievementsPage.Secret');
					}
					return t('achievementsPage.NotSecret');
				},
			},
			{
				title: 'Actions',
				width: 100,
				render: (record: AchievementDefinition) => (
					<Dropdown overlay={getActionMenu(record)} trigger={['click']}>
						<EllipsisOutlined onClick={(e) => e.preventDefault()} />
					</Dropdown>
				),
			},
		];
	}, []);
	const loadAchievements = async () => {
		var achievements = await api.listAchievements(Number.parseInt(projectId));
		setAchievements(achievements);
	};
	useEffect(() => {
		loadAchievements();
	}, []);

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: 'game-services',
				breadcrumbName: 'Game Services',
			},
			{ path: '', breadcrumbName: t('achievementsPage.Header') },
		]);
	}, [projectInfo?.id, projectInfo?.name]);

	const getActionMenu = (record: AchievementDefinition) => {
		return (
			<Menu>
				<Menu.Item
					key="edit"
					onClick={() => {
						history.push(`${route.url}/${record.name}/edit`);
					}}
				>
					{t('achievementsPage.actions.edit')}
				</Menu.Item>
				<Menu.Item
					key="delete"
					danger={true}
					onClick={() => {
						openDeleteModal(record);
					}}
				>
					{t('achievementsPage.actions.delete')}
				</Menu.Item>
			</Menu>
		);
	};

	const openDeleteModal = (record: AchievementDefinition) => {
		Modal.confirm({
			icon: <InfoCircleOutlined style={{ color: 'red' }} />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('achievementsPage.actions.deleteModal.header')}
				</span>
			),
			okText: t('achievementsPage.actions.deleteModal.okButton'),
			cancelText: t('common.cancel'),
			onOk() {
				return handleDeleteAchievement(record);
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: (
				<span>
					{t('achievementsPage.actions.deleteModal.body', {
						apiName: record.name,
					})}
				</span>
			),
		});
	};

	const handleDeleteAchievement = async (record: AchievementDefinition) => {
		if (!record.id) {
			return;
		}
		try {
			await api.deleteAchievements(record.id);
			OmniNotification.success({
				message: t('achievementsPage.actions.deleteMessage'),
				description: t('achievementsPage.actions.deleteDescription', {
					apiName: record.name,
				}),
			});
			loadAchievements();
		} catch (e) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
		}
	};

	return (
		<>
			<div className={styles.pageContainer}>
				<Breadcrumb routes={routes} />
				<div className="page-head">
					<div>
						<h2 className="h2" data-testid="page-title">
							{t('achievementsPage.Header')}
						</h2>
					</div>
					<div className={styles.buttonContainer}>
						<Button
							type="primary"
							data-testid="create-achievement-button"
							onClick={() => navigateToCreateAchievement()}
						>
							{t('achievementsPage.Create')}
						</Button>
					</div>
				</div>
				{achievements == null && <Spin />}
				{achievements != null && achievements.length == 0 && (
					<div
						className={styles.noAchievements}
						data-testid="no-achievements-message"
					>
						<Trans i18nKey="achievementsPage.NoAchievement" />
					</div>
				)}
				{achievements != null && achievements.length > 0 && (
					<div>
						<Table
							className="table"
							size="middle"
							rowKey="id"
							loading={false}
							rowClassName={styles.tableRow}
							columns={columns}
							dataSource={achievements}
							pagination={false}
							data-testid="achievements-table"
						/>
					</div>
				)}
			</div>
		</>
	);
}
